import { create } from 'zustand';
import type { SidebarChoice } from './sidebarLayout';
import { DEFAULT_DEAL_FILTERS, type DealFilters } from '@shared/flow/account-hierarchy-deal-model';
import {
  DEFAULT_LENS_FLAGS, DEFAULT_RENEWAL_FILTER, type HealthKey, type HierarchyLensKey, type LensFlags, type RenewalFilter,
} from '@shared/flow/account-hierarchy-filters';
import { WHITESPACE_STATUSES, type WhitespaceStatus } from '@shared/flow/account-hierarchy-whitespace';

/**
 * Hierarchy tab view state: view mode (Table | Graph), the lens toggles (Deals, Renewals; they combine with the always-on Customer status legend selection) and each
 * lens's own filters. Toggling a lens never clears any filters. Module-scope zustand so the choices survive the tab remounting on
 * every J/K step between accounts (same reasoning as `shell-next/account/account-lens-layout-store.ts`);
 * a page reload resets everything (graph, Deals on, Open deals). Data-only state — the writers are
 * module functions so `useAccountHierarchyViewStore.setState(initialState, true)` (the CLAUDE.md
 * test-reset contract) cannot strip behavior.
 */
export type HierarchyViewMode = 'table' | 'graph';

interface AccountHierarchyViewState {
  viewMode: HierarchyViewMode;
  /** Which lenses are on (independent toggles). Highlight = intersection of the ON lenses and the legend selection. */
  lensOn: LensFlags;
  /** Deals lens filters. */
  dealFilters: DealFilters;
  /** Renewals lens: renewal date window (presets + custom range); 'any' = every renewal date. */
  renewalFilter: RenewalFilter;
  /** Renewals lens: health rating filter (empty = any). */
  healthFilter: HealthKey[];
  /** Customer status lens (always on; cards are always tinted): highlight only these statuses (empty = none picked, nothing dims). */
  legendStatuses: WhitespaceStatus[];
  /** Filters sidebar open/closed as the rep last set it, per view (null = never chosen: the default follows width / full screen). */
  sidebarChoice: SidebarChoice;
  /** Table: hide non-matching rows instead of dimming them. */
  showOnlyMatching: boolean;
  /** Table: sort siblings by a Deal column (null = tree order). */
  dealSort: DealSort | null;
}

export type DealSortKey = 'dealAmount' | 'dealArr';
export interface DealSort {
  key: DealSortKey;
  dir: 'asc' | 'desc';
}

export const initialState: AccountHierarchyViewState = {
  viewMode: 'graph',
  lensOn: DEFAULT_LENS_FLAGS,
  dealFilters: DEFAULT_DEAL_FILTERS,
  renewalFilter: DEFAULT_RENEWAL_FILTER,
  healthFilter: [],
  legendStatuses: [],
  sidebarChoice: { inline: null, fullscreen: null },
  showOnlyMatching: false,
  dealSort: null,
};

export const useAccountHierarchyViewStore = create<AccountHierarchyViewState>(() => initialState);

export function setHierarchyViewMode(viewMode: HierarchyViewMode): void {
  useAccountHierarchyViewStore.setState({ viewMode });
}

export function setLensOn(lens: HierarchyLensKey, on: boolean): void {
  useAccountHierarchyViewStore.setState(s => ({ lensOn: { ...s.lensOn, [lens]: on } }));
}

export function patchDealFilters(patch: Partial<DealFilters>): void {
  useAccountHierarchyViewStore.setState(s => ({ dealFilters: { ...s.dealFilters, ...patch } }));
}

export function setSidebarOpen(view: 'inline' | 'fullscreen', open: boolean): void {
  useAccountHierarchyViewStore.setState(s => ({ sidebarChoice: { ...s.sidebarChoice, [view]: open } }));
}

export function setRenewalFilter(renewalFilter: RenewalFilter): void {
  useAccountHierarchyViewStore.setState({ renewalFilter });
}

export function setHealthFilter(healthFilter: HealthKey[]): void {
  useAccountHierarchyViewStore.setState({ healthFilter });
}

/** Every lens's filters back to their defaults; which lenses are on, the view mode, sidebar choice and table sort are kept. */
export function resetAllFilters(): void {
  useAccountHierarchyViewStore.setState({
    dealFilters: initialState.dealFilters,
    renewalFilter: initialState.renewalFilter,
    healthFilter: initialState.healthFilter,
    legendStatuses: initialState.legendStatuses,
    showOnlyMatching: initialState.showOnlyMatching,
  });
}

export function setLegendStatuses(legendStatuses: WhitespaceStatus[]): void {
  useAccountHierarchyViewStore.setState({ legendStatuses });
}

export function toggleLegendStatus(status: WhitespaceStatus): void {
  useAccountHierarchyViewStore.setState(s => ({
    legendStatuses: WHITESPACE_STATUSES.filter(x => (x === status ? !s.legendStatuses.includes(x) : s.legendStatuses.includes(x))),
  }));
}

export function setShowOnlyMatching(showOnlyMatching: boolean): void {
  useAccountHierarchyViewStore.setState({ showOnlyMatching });
}

/** Header click: descending, then ascending, then back to tree order; another column starts descending. */
export function cycleDealSort(key: DealSortKey): void {
  useAccountHierarchyViewStore.setState(s => {
    if (s.dealSort?.key !== key) return { dealSort: { key, dir: 'desc' } };
    return { dealSort: s.dealSort.dir === 'desc' ? { key, dir: 'asc' } : null };
  });
}
